import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { NotesDialogState, NotesEntityType } from '../types/notes.types';

interface OpenEntityNotesDialogInput {
    entityType: NotesEntityType;
    entityId: string;
    entityName?: string;
}

interface EntityNotesDialogContextValue {
    dialogState: NotesDialogState;
    openDialog: (input: OpenEntityNotesDialogInput) => void;
    closeDialog: () => void;
}

const EntityNotesDialogContext = createContext<EntityNotesDialogContextValue | null>(null);

export const useEntityNotesDialog = (): EntityNotesDialogContextValue => {
    const ctx = useContext(EntityNotesDialogContext);
    if (!ctx) {
        throw new Error('useEntityNotesDialog must be used within a <EntityNotesDialogProvider>');
    }

    return ctx;
};

interface EntityNotesDialogProviderProps {
    children: React.ReactNode;
}

export const EntityNotesDialogProvider: React.FC<EntityNotesDialogProviderProps> = ({ children }) => {
    const [dialogState, setDialogState] = useState<NotesDialogState>({
        open: false,
        entityType: null,
        entityId: null,
        entityName: null,
    });

    const openDialog = useCallback(({ entityType, entityId, entityName }: OpenEntityNotesDialogInput) => {
        setDialogState({
            open: true,
            entityType,
            entityId,
            entityName: entityName || null,
        });
    }, []);

    const closeDialog = useCallback(() => {
        setDialogState({
            open: false,
            entityType: null,
            entityId: null,
            entityName: null,
        });
    }, []);

    const value = useMemo(() => ({
        dialogState,
        openDialog,
        closeDialog,
    }), [dialogState, openDialog, closeDialog]);

    return (
        <EntityNotesDialogContext.Provider value={value}>
            {children}
        </EntityNotesDialogContext.Provider>
    );
};
